import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AttachmentUploadSection from "./AttachmentUploadSection";
import SectionList from "./SectionList";
import SectionNavigator from "./SectionNavigator";
import AutosaveIndicator from "../common/AutosaveIndicator";
import useE2ESubmissionEditor from "../../hooks/useE2ESubmissionEditor";
import { useAutosave } from "../../hooks/useAutosave";
import { buildAnswersPayload } from "../../utils/e2eAnswers";
import { getSectionStatuses } from "../../utils/e2eRequiredValidation";
import {IDSButton, IDSButtonGroup, IDSAlert, IDSBreadcrumbs, IDSTooltip} from '@inera/ids-react';
import {
  createUserSubmission,
  getUserForm,
  submitUserSubmission,
  updateUserSubmission,
  uploadAttachment,
  getAttachments,
  deleteAttachment,
} from "../../services/E2EAPI";


function FormView() {
  const { isAuthenticated } = useAuth();
  const { formId } = useParams();
  const submissionIdRef = useRef(null);
  // True while a manual save/submit is running — blocks autosave from racing it.
  const savingRef = useRef(false);

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const {
    answers,
    attachments,
    submitting,
    error,
    draftMessage,
    uploadError,
    validationErrors,
    setError,
    setUploadError,
    setAttachments,
    setSubmitting,
    setDraftMessage,
    handleAnswerChange,
    validateRequired,
    validateZipFile,
    markDraftSavedNow,
  } = useE2ESubmissionEditor();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        const data = await getUserForm(formId);
        setForm(data);
      } catch (err) {
        console.error(err);
        setError("Kunde inte ladda formuläret.");
      } finally {
        setLoading(false);
      }
    };

    if (formId && isAuthenticated) {
      fetchForm();
    }
  }, [formId, isAuthenticated]);

  const saveOrUpdate = useCallback(async (status) => {
    const payload = {
      formId: Number(form.formId),
      status,
      answers: buildAnswersPayload(answers),
    };

    if (submissionIdRef.current) {
      if (status === "SUBMITTED") {
        // First update the answers via PUT, then call the dedicated submit endpoint
        await updateUserSubmission(
          submissionIdRef.current,
          { ...payload, status: "DRAFT" }
        );
        return submitUserSubmission(submissionIdRef.current);
      } else {
        // Saving draft — just update answers
        return updateUserSubmission(submissionIdRef.current, payload);
      }
    } else {
      // No existing submission — create a new one via POST
      const data = await createUserSubmission(payload);
      if (!data?.submissionId) throw new Error("Kunde inte spara inlämningen. Försök igen.");
      submissionIdRef.current = data.submissionId;
      return data;
    }
  }, [form, answers]);

  // Autosave: saves a draft 10 s after the last answer change. Skipped while a
  // manual save/submit is in flight so we never create duplicate submissions.
  const autosaveDraft = useCallback(async () => {
    if (savingRef.current || submitting || !form) return;
    savingRef.current = true;
    try {
      await saveOrUpdate("DRAFT");
    } finally {
      savingRef.current = false;
    }
  }, [saveOrUpdate, submitting, form]);

  const autosaveStatus = useAutosave(
    autosaveDraft,
    [answers],
    { delay: 10000, enabled: !!form && !loading }
  );

  // Live completeness per top-level section, used by the section navigator and
  // by the "missing answers" summary next to the submit button.
  const sectionStatus = useMemo(
    () => getSectionStatuses(form?.sections || [], answers),
    [form, answers]
  );
  const missingSections = sectionStatus.filter((s) => !s.complete);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formId) return;

    if (!validateRequired(form?.sections || [])) {
      setError("Du har obesvarade obligatoriska frågor. Kontrollera frågorna markerade nedan och försök igen.");
      // Scroll to the first question that failed validation
      setTimeout(() => {
        const firstError = document.querySelector('[data-question-error="true"]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    try {
      setSubmitting(true);
      savingRef.current = true;
      setError("");
      await saveOrUpdate("SUBMITTED");
      navigate("/e2e/tester/submissions", {
        state: { successMessage: "Formuläret skickades in!" },
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Formuläret kunde inte skickas in. Kontrollera din anslutning och försök igen.");
    } finally {
      savingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!formId) return;
    try {
      setSubmitting(true);
      savingRef.current = true;
      setDraftMessage("");
      setError("");
      await saveOrUpdate("DRAFT");
      markDraftSavedNow();
    } catch (err) {
      console.error(err);
      setError(err.message || "Utkastet kunde inte sparas. Kontrollera din anslutning och försök igen.");
    } finally {
      savingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;

    setUploadError("");

    if (!validateZipFile(file)) {
      return;
    }

    // Ensure we have a submission to attach to
    if (!submissionIdRef.current) {
      try {
        savingRef.current = true;
        await saveOrUpdate("DRAFT");
      } catch (err) {
        console.error(err);
        setUploadError("Kunde inte spara utkast innan filuppladdning.");
        return;
      } finally {
        savingRef.current = false;
      }
    }

    try {
      const uploaded = await uploadAttachment(submissionIdRef.current, file);
      setAttachments((prev) => [...prev, uploaded]);
    } catch (err) {
      console.error(err);
      setUploadError(err.message || "Filen kunde inte laddas upp. Kontrollera att filen är ett giltigt ZIP-arkiv och försök igen.");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await deleteAttachment(submissionIdRef.current, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error(err);
      setUploadError(err.message || "Bilagan kunde inte tas bort. Försök igen.");
    }
  };

  if (!formId) return <div>Inget formId angivet.</div>;
  if (loading) return <div>Laddar formulär...</div>;
  if (!form) return <div>Formulär hittades inte.</div>;

  const { formTitle, version, description, sections = [] } = form;

  return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: '1400px', margin: '2rem auto' }}>
        <div className="ids-mb-6">
          <IDSBreadcrumbs lead="Du är här:">
            <Link to="/start">Start</Link>
            <Link to="/e2e/tester">E2E Underlag</Link>
            <a>{formTitle} v{version}</a>
          </IDSBreadcrumbs>
        </div>
        <h2 className="ids-heading-l ids-mb-6">{formTitle} v{version}</h2>
        {description && <p>{description}</p>}
        <hr />

        <div className="ids-mb-4">
          <form onSubmit={handleSubmit}>
            <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <SectionList
                  sections={sections}
                  answers={answers}
                  onAnswerChange={handleAnswerChange}
                  validationErrors={validationErrors}
                />

                <AttachmentUploadSection
                  onFileUpload={handleFileUpload}
                  uploadError={uploadError}
                  attachments={attachments}
                  onDeleteAttachment={handleDeleteAttachment}
                />

                <IDSButtonGroup
                  justify="start"
                  unresponsive
                >
                  <IDSButton size="m" type="submit" disabled={submitting || missingSections.length > 0}>
                    {submitting ? "Skickar..." : "Skicka svar"}
                  </IDSButton>
                  <IDSButton secondary size="m" type="button" disabled={submitting} onClick={handleSaveDraft}>
                    {submitting ? "Sparar..." : "Spara utkast"}
                  </IDSButton>
                </IDSButtonGroup>

                {missingSections.length > 0 && (
                  <div
                    className="ids-mt-2"
                    style={{ color: "var(--ids-palette-error-40)", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                      Alla obligatoriska frågor och följdfrågor måste fyllas i.
                      <IDSTooltip
                        position="top"
                        maxWidth=""
                        trigger={
                          <span
                            aria-label="Visa vilka avsnitt som saknar svar"
                            className="ids-icon-information ids-icon--m ids-icon--color-preset-1 ids-icon--interactive"
                            role="button"
                            style={{ position: "relative", zIndex: 4 }}
                            tabIndex={0}
                          />
                        }
                      >
                        <strong>Saknar svar i:</strong>
                        <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
                          {missingSections.map((s) => (
                            <li key={s.sectionId}>{s.label} ({s.missing})</li>
                          ))}
                        </ul>
                      </IDSTooltip>
                    </span>
                  </div>
                )}

                {/* Manual draft message — hidden while the autosave indicator is showing */}
                <div style={{ marginTop: "1rem", minHeight: "1.5rem" }}>
                  {autosaveStatus === "idle" && draftMessage && (
                    <IDSAlert ribbon type="success">
                      {draftMessage}
                    </IDSAlert>
                  )}
                </div>
                {error && (
                  <div style={{ marginTop: "1rem" }}>
                    <IDSAlert ribbon dismissible type="error">
                      {error}
                    </IDSAlert>
                  </div>
                )}
              </div>

              <SectionNavigator sectionStatus={sectionStatus} />
            </div>
          </form>
        </div>
        {/* Fixed top-right indicator — always visible regardless of scroll position */}
        <AutosaveIndicator status={autosaveStatus} fixed />
      </main>
    </div>
  );
}

export default FormView;